import {
  Box,
  Button,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  useDisclosure,
  Text,
  useToast,
} from '@chakra-ui/react';
import React, { useEffect } from 'react';

import {
  useGetDataPipelineDownloadSignedUrlLazyQueryGQLType,
  useGetDataPipelineDownloadSignedUrlQuery,
} from '../../requests/dataPipeline.gql.generated';
import {
  DownloadIconButton,
  IconButtonProps,
} from '~/packages/common-frontend/components/IconButton';
import { Icon } from '~/packages/common-frontend/components/icons';
import { errorToastConfig } from '~/packages/common-frontend/constants';

type DownloadItemProps = {
  displayText: string;
  filename: string;
  url: string;
};
const downloadUrl = ({
  url,
  filename,
}: {
  url: string;
  filename: string;
}): void => {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
};

const DownloadItem = ({ displayText, filename, url }: DownloadItemProps) => {
  return (
    <MenuItem>
      <HStack onClick={() => downloadUrl({ url, filename })}>
        <Text>{displayText}</Text>

        <DownloadIconButton
          isDisabled={!url}
          ariaLabel="download"
          label="Download"
        />
      </HStack>
    </MenuItem>
  );
};

export const DownloadMenuList = (props: { artifactInstanceId: string }) => {
  const toast = useToast();
  const { artifactInstanceId } = props;
  const { data, error } = useGetDataPipelineDownloadSignedUrlQuery({
    variables: {
      serializedParams: JSON.stringify({
        artifactInstanceId,
      }),
    },

    // TTL on this signed URL is 4 hours.
    // - Always fetch a fresh URL when the download menu is reopened
    // - Refresh the URL every hour the download menu remains open
    fetchPolicy: 'network-only',
    pollInterval: 60 * 60 * 1000,
  });

  useEffect(() => {
    if (error) {
      toast({ ...errorToastConfig, title: error.message });
    }
  }, [error, toast]);

  if (!data) {
    return (
      <MenuList>
        <MenuItem>Loading...</MenuItem>
      </MenuList>
    );
  }

  if (data.downloadUrls.length === 0) {
    return (
      <MenuList>
        <MenuItem>No downloads available</MenuItem>
      </MenuList>
    );
  }

  return (
    <MenuList>
      {data.downloadUrls.map(({ url, filename }) => (
        <DownloadItem
          key={url}
          url={url}
          filename={filename}
          displayText={filename}
        />
      ))}
    </MenuList>
  );
};

export const DownloadMenuButton = ({
  artifactInstanceId,
  buttonLabel,
}: {
  buttonLabel: string;
  artifactInstanceId: string;
}) => {
  // Utilize Chakra's useDisclosure hook to manage the open state of the menu
  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <Box>
      <Menu isOpen={isOpen} onOpen={onOpen} onClose={onClose}>
        <MenuButton
          as={Button}
          leftIcon={<Icon icon="download" />}
          variant="ghost"
          border="2px solid"
          onClick={() => !isOpen && onOpen()} // Ensures onOpen is called when the menu is not already open
        >
          {buttonLabel || 'Download'}
        </MenuButton>
        {isOpen && <DownloadMenuList artifactInstanceId={artifactInstanceId} />}
      </Menu>
    </Box>
  );
};

/**
 * Downloads an artifact instance's files in one click. The signed URLs expire, so
 * each click mints fresh ones.
 */
export const DownloadInstanceIconButton = ({
  artifactInstanceId,
  label,
  size,
}: {
  artifactInstanceId: string;
  label: string;
  /** Chakra's button size; the default when omitted. */
  size?: IconButtonProps['size'];
}): React.ReactElement => {
  const toast = useToast();
  const [
    fetchUrls,
    { loading },
  ] = useGetDataPipelineDownloadSignedUrlLazyQueryGQLType({
    fetchPolicy: 'network-only',
  });

  const download = async (): Promise<void> => {
    const { data, error } = await fetchUrls({
      variables: { serializedParams: JSON.stringify({ artifactInstanceId }) },
    });
    if (error || !data) {
      toast({
        ...errorToastConfig,
        title: error?.message ?? 'Could not download the file',
      });
      return;
    }
    if (data.downloadUrls.length === 0) {
      toast({ ...errorToastConfig, title: 'No downloads available' });
      return;
    }
    data.downloadUrls.forEach(downloadUrl);
  };

  return (
    <DownloadIconButton
      ariaLabel={`download ${label}`}
      label={`Download ${label}`}
      isLoading={loading}
      size={size}
      onClick={() => download()}
    />
  );
};
